/**
 * The typed columns one entry has, per family: migration 0002's slot columns
 * on data.entry, and since 0089 four more number columns (n7 to n10). A leaf module, because the authoring (`compose-author.ts`)
 * and the template validator (`spec/validate-spec.ts`) both read it,
 * and the validator cannot import the authoring.
 *
 *   n  numbers: money, number, quantity, a worked-out number
 *   t  text
 *   d  dates
 *   r  a choice or a reference (a select, a reference to a list)
 *   b  yes-or-no
 */
export const ENTRY_SLOT_COUNT = { n: 10, t: 6, d: 4, r: 6, b: 2 } as const satisfies Readonly<
  Record<'n' | 't' | 'd' | 'r' | 'b', number>
>;

export type EntrySlotLetter = keyof typeof ENTRY_SLOT_COUNT;

/** Every slot column of one family, in order: `n1` to `n10` for the numbers. */
export const entrySlotsOf = (letter: EntrySlotLetter): readonly string[] =>
  Array.from({ length: ENTRY_SLOT_COUNT[letter] }, (_, at) => `${letter}${at + 1}`);

/**
 * Every slot column data.entry holds. The guard a column name passes before
 * it is put into SQL, so it names only columns that exist.
 */
export const ENTRY_SLOT_COLUMNS: ReadonlySet<string> = new Set(
  (Object.keys(ENTRY_SLOT_COUNT) as EntrySlotLetter[]).flatMap((letter) => entrySlotsOf(letter)),
);

/** True when `slot` is a slot column of the family `letter`. */
export const isEntrySlotOf = (letter: EntrySlotLetter, slot: string | null | undefined): slot is string =>
  typeof slot === 'string' && slot.startsWith(letter) && ENTRY_SLOT_COLUMNS.has(slot);

/**
 * Which families may spill into the attrs document when full. Text, a date
 * past the time axis, and a yes-or-no lose nothing there. A number does: no
 * total, sort or filter reads attrs, so an eleventh money field is refused
 * rather than kept where nothing adds it up. A select or a reference holds a
 * real key, which the registry never allows in a document, and a formula's
 * whole reason for keeping its answer is that a slot can be read.
 */
export const ENTRY_SLOT_SPILLS = ['t', 'd', 'b'] as const satisfies readonly EntrySlotLetter[];
